/**
 * WP-V2-HO — HouseScene: interaktywny pokój (PLAN-V2 §6 House + §10).
 * Konsumuje AnimatedDogAvatar, ObjectLayer, WorldCamera+WalkController.
 * Build-mode (HouseBuild) i wejście do ConsoleHub zachowane.
 */
import Phaser from 'phaser';
import { GRID_COLS, GRID_ROWS, TILE_PX, type PlacedBlock } from '@pieskowo/shared';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { GridBoard } from '../../core/build/GridBoard.js';
import { AnimatedDogAvatar } from '../../avatar/AnimatedDogAvatar.js';
import { OutfitStore } from '../../avatar/outfitStore.js';
import {
  ObjectLayer,
  playFx,
  type InteractEvent,
  type InteractTarget,
} from '../../interact/index.js';
import { setupWorld, follow, WalkController } from '../../core/world/index.js';
import { blockColor, blockLabelPl } from './blockVisuals.js';
import { loadHouseLayout, type HouseLoadResult } from './houseApi.js';
import {
  INTERACT_BUS_EVENT,
  houseMissionFromInteract,
} from './missionHook.js';
import {
  HOUSE_WORLD_H,
  HOUSE_WORLD_W,
  HOUSE_LABELS_PL,
  HOUSE_TEXTURE_SHAPES,
  bedAction,
  feedFromBowl,
  fridgeTreatSpawnCount,
  houseObjectDefs,
  housePlacements,
  houseShapeForTexture,
  houseSnapZones,
  houseTextureKeys,
  isConsoleEntry,
  isWindow,
  pourFoodIntoBowl,
  treatSpawnOffsets,
  type BowlFill,
  type FridgeDoor,
  type HouseDecorKind,
  type PowerState,
} from './objects.js';

export type HouseSceneData = {
  houseId?: string;
};

const HOUSE_TEX_SIZE = 160;

/**
 * Upewnij się, że wszystkie tekstury domu istnieją.
 * PNG z Preload mają pierwszeństwo; brak → narysuj proceduralnie (bez emoji).
 */
export function ensureHouseTextures(scene: Phaser.Scene): void {
  for (const key of houseTextureKeys()) {
    ensureHouseTexture(scene, key);
  }
  for (const def of houseObjectDefs()) {
    ensureHouseTexture(scene, def.texture);
    if (def.states) {
      for (const s of def.states) {
        const stateKey = `obj_${def.id}_${s}`;
        if (!scene.textures.exists(stateKey)) {
          const kind =
            houseShapeForTexture(def.texture) ??
            HOUSE_TEXTURE_SHAPES[def.texture];
          if (kind) {
            const g = scene.make.graphics({ x: 0, y: 0 });
            drawHouseShape(g, kind, HOUSE_TEX_SIZE, HOUSE_TEX_SIZE);
            g.generateTexture(stateKey, HOUSE_TEX_SIZE, HOUSE_TEX_SIZE);
            g.destroy();
          }
        }
      }
    }
  }
}

export function ensureHouseTexture(
  scene: Phaser.Scene,
  textureKey: string,
): string {
  if (scene.textures.exists(textureKey)) return textureKey;
  const kind = houseShapeForTexture(textureKey);
  if (!kind) return textureKey;
  const g = scene.make.graphics({ x: 0, y: 0 });
  drawHouseShape(g, kind, HOUSE_TEX_SIZE, HOUSE_TEX_SIZE);
  g.generateTexture(textureKey, HOUSE_TEX_SIZE, HOUSE_TEX_SIZE);
  g.destroy();
  return textureKey;
}

export function drawHouseShape(
  g: Phaser.GameObjects.Graphics,
  kind: HouseDecorKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;
  switch (kind) {
    case 'food_bag':
      drawFoodBag(g, cx, cy);
      break;
    case 'bowl':
      drawBowl(g, cx, cy);
      break;
    case 'fridge':
      drawFridge(g, cx, cy);
      break;
    case 'treat':
      drawTreat(g, cx, cy);
      break;
    case 'tv':
      drawTv(g, cx, cy);
      break;
    case 'console':
      drawConsole(g, cx, cy);
      break;
    case 'bed':
      drawBed(g, cx, cy);
      break;
    case 'blocks_box':
      drawBlocksBox(g, cx, cy);
      break;
    case 'figure_red':
      drawFigure(g, cx, cy, 0xef5350);
      break;
    case 'figure_blue':
      drawFigure(g, cx, cy, 0x42a5f5);
      break;
    case 'figure_green':
      drawFigure(g, cx, cy, 0x66bb6a);
      break;
    case 'figure_yellow':
      drawFigure(g, cx, cy, 0xffee58);
      break;
    case 'lamp':
      drawLamp(g, cx, cy);
      break;
    case 'window':
      drawWindow(g, cx, cy);
      break;
    case 'cat':
      drawCat(g, cx, cy);
      break;
  }
}

function drawFoodBag(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xd7ccc8, 1);
  g.fillRoundedRect(cx - 32, cy - 40, 64, 80, 10);
  g.fillStyle(0xffcc80, 1);
  g.fillRoundedRect(cx - 28, cy - 20, 56, 44, 8);
  g.fillStyle(0xffe0b2, 0.8);
  g.fillEllipse(cx, cy - 28, 40, 18);
  g.fillStyle(0x8d6e63, 1);
  g.fillCircle(cx, cy + 4, 10);
  g.fillStyle(0xffffff, 0.5);
  g.fillEllipse(cx - 8, cy - 36, 16, 10);
}

function drawBowl(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xb0bec5, 1);
  g.fillEllipse(cx, cy + 10, 80, 36);
  g.fillStyle(0x90a4ae, 1);
  g.fillEllipse(cx, cy, 70, 28);
  g.fillStyle(0xcfd8dc, 1);
  g.fillEllipse(cx, cy - 6, 54, 18);
  g.fillStyle(0xffcc80, 0.9);
  g.fillEllipse(cx, cy - 4, 30, 10);
}

function drawFridge(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xe3f2fd, 1);
  g.fillRoundedRect(cx - 36, cy - 52, 72, 100, 8);
  g.lineStyle(3, 0x90caf9, 1);
  g.strokeRoundedRect(cx - 36, cy - 52, 72, 100, 8);
  g.fillStyle(0xbbdefb, 1);
  g.fillRect(cx - 30, cy - 8, 60, 4);
  g.fillStyle(0x64b5f6, 1);
  g.fillRoundedRect(cx + 22, cy - 30, 6, 20, 2);
  g.fillRoundedRect(cx + 22, cy + 10, 6, 20, 2);
  g.fillStyle(0xffffff, 0.6);
  g.fillRect(cx - 28, cy - 44, 20, 28);
}

function drawTreat(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xffe0b2, 1);
  g.fillEllipse(cx, cy, 48, 28);
  g.fillStyle(0xffcc80, 1);
  g.fillCircle(cx - 16, cy, 14);
  g.fillCircle(cx + 16, cy, 14);
  g.fillStyle(0xfff3e0, 0.7);
  g.fillEllipse(cx, cy - 4, 18, 10);
}

function drawTv(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0x546e7a, 1);
  g.fillRoundedRect(cx - 48, cy - 36, 96, 68, 8);
  g.fillStyle(0x263238, 1);
  g.fillRoundedRect(cx - 40, cy - 28, 80, 48, 4);
  g.fillStyle(0x4fc3f7, 0.85);
  g.fillRoundedRect(cx - 34, cy - 22, 68, 36, 3);
  g.fillStyle(0x90a4ae, 1);
  g.fillRect(cx - 8, cy + 32, 16, 12);
  g.fillEllipse(cx, cy + 46, 40, 10);
}

function drawConsole(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0x7e57c2, 1);
  g.fillRoundedRect(cx - 40, cy - 16, 80, 40, 12);
  g.fillStyle(0xb39ddb, 1);
  g.fillCircle(cx - 18, cy, 10);
  g.fillCircle(cx + 18, cy, 10);
  g.fillStyle(0xffffff, 0.9);
  g.fillCircle(cx - 18, cy, 4);
  g.fillCircle(cx + 18, cy, 4);
  g.fillStyle(0xff80ab, 1);
  g.fillCircle(cx + 4, cy - 6, 4);
  g.fillCircle(cx + 12, cy + 4, 4);
}

function drawBed(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xf48fb1, 1);
  g.fillRoundedRect(cx - 48, cy - 8, 96, 40, 10);
  g.fillStyle(0xfce4ec, 1);
  g.fillRoundedRect(cx - 40, cy - 28, 36, 28, 8);
  g.fillStyle(0xf8bbd0, 1);
  g.fillRoundedRect(cx - 4, cy - 4, 44, 28, 8);
  g.fillStyle(0xce93d8, 0.5);
  g.fillEllipse(cx, cy + 36, 70, 14);
}

function drawBlocksBox(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xffb74d, 1);
  g.fillRoundedRect(cx - 40, cy - 24, 80, 56, 8);
  g.fillStyle(0xffe0b2, 1);
  g.fillRect(cx - 40, cy - 24, 80, 14);
  g.fillStyle(0xef5350, 1);
  g.fillRect(cx - 24, cy - 4, 16, 16);
  g.fillStyle(0x42a5f5, 1);
  g.fillRect(cx - 4, cy, 16, 16);
  g.fillStyle(0x66bb6a, 1);
  g.fillRect(cx + 14, cy - 6, 16, 16);
}

function drawFigure(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  color: number,
): void {
  g.fillStyle(color, 1);
  g.fillRoundedRect(cx - 18, cy - 22, 36, 44, 6);
  g.fillStyle(0xffffff, 0.45);
  g.fillRect(cx - 12, cy - 14, 24, 8);
  g.lineStyle(2, 0xffffff, 0.9);
  g.strokeRoundedRect(cx - 18, cy - 22, 36, 44, 6);
}

function drawLamp(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xfff59d, 0.95);
  g.fillEllipse(cx, cy - 10, 56, 40);
  g.fillStyle(0xffe082, 1);
  g.fillTriangle(cx - 28, cy - 4, cx + 28, cy - 4, cx, cy - 36);
  g.fillStyle(0xbcaaa4, 1);
  g.fillRect(cx - 4, cy + 4, 8, 36);
  g.fillEllipse(cx, cy + 42, 28, 10);
  g.fillStyle(0xfffde7, 0.5);
  g.fillCircle(cx - 8, cy - 16, 8);
}

function drawWindow(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xffe082, 1);
  g.fillRoundedRect(cx - 40, cy - 40, 80, 80, 6);
  g.fillStyle(0x81d4fa, 1);
  g.fillRect(cx - 32, cy - 32, 30, 30);
  g.fillRect(cx + 2, cy - 32, 30, 30);
  g.fillRect(cx - 32, cy + 2, 30, 30);
  g.fillRect(cx + 2, cy + 2, 30, 30);
  g.fillStyle(0xffffff, 0.5);
  g.fillRect(cx - 28, cy - 28, 10, 18);
}

function drawCat(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
  g.fillStyle(0xffcc80, 1);
  g.fillEllipse(cx, cy + 6, 52, 36);
  g.fillCircle(cx + 18, cy - 10, 18);
  g.fillTriangle(cx + 8, cy - 24, cx + 14, cy - 10, cx + 2, cy - 12);
  g.fillTriangle(cx + 28, cy - 24, cx + 22, cy - 10, cx + 34, cy - 12);
  g.fillStyle(0xffffff, 1);
  g.fillCircle(cx + 14, cy - 12, 3);
  g.fillCircle(cx + 24, cy - 12, 3);
  g.fillStyle(0x5d4037, 1);
  g.fillCircle(cx + 14, cy - 12, 1.5);
  g.fillCircle(cx + 24, cy - 12, 1.5);
  g.fillStyle(0xffab91, 1);
  g.fillTriangle(cx + 18, cy - 6, cx + 14, cy - 2, cx + 22, cy - 2);
  // ogon
  g.lineStyle(6, 0xffb74d, 1);
  g.lineBetween(cx - 24, cy + 8, cx - 36, cy - 16);
}

export class HouseScene extends BaseScene {
  static readonly KEY = 'House';

  private board = new GridBoard();
  private originX = 0;
  private originY = 0;
  private layout: PlacedBlock[] = [];
  private layoutVersion = 0;
  private houseId: string | null = null;
  private isOwner = true;
  private statusText: Phaser.GameObjects.Text | null = null;
  private furnitureLayer: Phaser.GameObjects.Container | null = null;

  private avatar: AnimatedDogAvatar | null = null;
  private walk: WalkController | null = null;
  private objectLayer: ObjectLayer | null = null;
  private interactTarget: InteractTarget | null = null;

  private bowlFill: BowlFill = 'empty';
  private fridgeDoor: FridgeDoor = 'closed';
  private fridgeTreatsSpawned = false;
  private tvOn: PowerState = 'off';
  private lampOn: PowerState = 'on';

  private tvScreen: Phaser.GameObjects.Rectangle | null = null;
  private tvGlow: Phaser.GameObjects.Ellipse | null = null;
  private tvColorTimer: Phaser.Time.TimerEvent | null = null;
  private lightDim: Phaser.GameObjects.Rectangle | null = null;
  private navPainted = false;
  private keys: {
    up?: Phaser.Input.Keyboard.Key;
    down?: Phaser.Input.Keyboard.Key;
    left?: Phaser.Input.Keyboard.Key;
    right?: Phaser.Input.Keyboard.Key;
    w?: Phaser.Input.Keyboard.Key;
    a?: Phaser.Input.Keyboard.Key;
    s?: Phaser.Input.Keyboard.Key;
    d?: Phaser.Input.Keyboard.Key;
  } = {};
  private unsubLayer: (() => void) | null = null;
  private pointerBound = false;

  constructor() {
    super(HouseScene.KEY);
  }

  init(data: HouseSceneData): void {
    this.houseId = data?.houseId ?? null;
  }

  preload(): void {
    this.preloadRealBg('bg_house');
    // Meble domu (Higgsfield) — klucz == nazwa pliku; brak → fallback proceduralny.
    for (const o of ['bed', 'bowl', 'fridge', 'tv', 'lamp', 'console', 'food_bag', 'window']) {
      this.load.image(`obj_house_${o}`, `/assets/real/obj_house_${o}.png`);
    }
  }

  create(): void {
    setupWorld(this, {
      worldW: HOUSE_WORLD_W,
      worldH: HOUSE_WORLD_H,
      bg: 0xffe0b2,
    });

    // PNG z preload; brak → proceduralne kształty (bez emoji-kwadratów)
    try {
      ensureHouseTextures(this);
    } catch {
      /* headless */
    }

    // tło na cały świat 1600×720
    if (this.textures.exists('bg_house')) {
      this.add
        .image(HOUSE_WORLD_W / 2, HOUSE_WORLD_H / 2, 'bg_house')
        .setDisplaySize(HOUSE_WORLD_W, HOUSE_WORLD_H)
        .setDepth(-1000);
    } else {
      this.add
        .rectangle(
          HOUSE_WORLD_W / 2,
          HOUSE_WORLD_H / 2,
          HOUSE_WORLD_W,
          HOUSE_WORLD_H,
          0xffe0b2,
        )
        .setDepth(-1000);
    }

    // podłoga salonu
    this.add
      .rectangle(HOUSE_WORLD_W / 2, HOUSE_WORLD_H - 80, HOUSE_WORLD_W, 200, 0xfff3e0, 0.55)
      .setDepth(-900);

    this.originX = (HOUSE_WORLD_W - GRID_COLS * TILE_PX) / 2;
    this.originY = 80;

    this.add
      .text(HOUSE_WORLD_W / 2, 28, 'Twój domek', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '32px',
        color: '#5d4037',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(50);

    this.statusText = this.add
      .text(this.gameW / 2, this.gameH - 28, 'Ładowanie domku…', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#6d4c41',
        backgroundColor: '#ffffffcc',
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);

    this.drawFloorGrid();
    this.furnitureLayer = this.add.container(0, 0).setDepth(5);

    // przyciemnienie lampy (off = widoczne)
    this.lightDim = this.add
      .rectangle(HOUSE_WORLD_W / 2, HOUSE_WORLD_H / 2, HOUSE_WORLD_W, HOUSE_WORLD_H, 0x1a1030, 0)
      .setDepth(9000)
      .setScrollFactor(1);

    this.spawnDogAndSystems();
    this.paintNav();
    this.navPainted = true;
    this.bindKeys();
    this.bindFloorTap();

    void this.bootstrap();
  }

  private spawnDogAndSystems(): void {
    const cache = OutfitStore.load();
    const breed =
      cache.breed || (this.registry.get('dogBreed') as string) || 'maltese';
    const color =
      cache.color || (this.registry.get('dogColor') as string) || undefined;
    const startX = HOUSE_WORLD_W / 2;
    const startY = 480;

    this.avatar = new AnimatedDogAvatar({
      scene: this,
      x: startX,
      y: startY,
      breed,
      color,
      scale: 0.95,
      slots: cache.slots,
      swimSlots: cache.swimSlots,
      mode: 'normal',
      depth: startY + 20,
    });

    this.walk = new WalkController(this, this.avatar, {
      enableInput: false,
      bounds: {
        minX: 48,
        minY: 160,
        maxX: HOUSE_WORLD_W - 48,
        maxY: HOUSE_WORLD_H - 48,
      },
      speed: 180,
    });

    follow(this, this.avatar.container, {
      lerp: 0.12,
      deadzone: { width: 100, height: 70 },
    });

    const walk = this.walk;
    const avatar = this.avatar;
    this.interactTarget = {
      moveTo: (x, y) => walk.moveTo(x, y),
      holdItem: (tex) => avatar.holdItem(tex),
      playAction: (a) =>
        avatar.playAction(
          a as 'sit' | 'jump' | 'wave' | 'eat' | 'swim' | 'sleep' | 'happy',
        ),
      getPosition: () => walk.position,
      get x() {
        return walk.position.x;
      },
      get y() {
        return walk.position.y;
      },
      get isMoving() {
        return walk.isMoving;
      },
    };

    this.objectLayer = new ObjectLayer({
      scene: this,
      defs: houseObjectDefs(),
      target: this.interactTarget,
      interactRange: 100,
      groundBounds: {
        minX: 48,
        maxX: HOUSE_WORLD_W - 48,
        minY: 360,
        maxY: HOUSE_WORLD_H - 48,
      },
    });
    this.objectLayer.setSnapZones(houseSnapZones());
    this.objectLayer.spawn(housePlacements());
    this.paintObjectLabels();
    this.ensureBowlVisual();
    this.ensureTvScreen();

    this.unsubLayer = this.objectLayer.on((evt) => this.onInteract(evt));
  }

  private paintObjectLabels(): void {
    // tylko etykiety PL — bez emoji-placeholderów na teksturach (G9/Toca)
    for (const obj of this.objectLayer?.getObjects() ?? []) {
      const label = HOUSE_LABELS_PL[obj.def.id];
      if (!label) continue;
      this.add
        .text(obj.x, obj.y + obj.def.size / 2 + 10, label, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#4a3f55',
          fontStyle: 'bold',
          backgroundColor: '#ffffffaa',
          padding: { x: 4, y: 2 },
        })
        .setOrigin(0.5, 0)
        .setDepth(obj.y + 1);
    }
  }

  private ensureBowlVisual(): void {
    // stany miski: empty na starcie (ObjectLayer już spawnuje)
    this.bowlFill = 'empty';
  }

  private ensureTvScreen(): void {
    const tv = this.objectLayer?.getObjects().find((o) => o.def.id === 'house_tv');
    if (!tv) return;
    this.tvScreen = this.add
      .rectangle(tv.x, tv.y - 8, 52, 36, 0x111111, 1)
      .setStrokeStyle(2, 0x455a64)
      .setDepth(tv.y + 2);
    this.tvGlow = this.add
      .ellipse(tv.x, tv.y - 8, 60, 40, 0x4fc3f7, 0)
      .setDepth(tv.y + 1);
    this.tvOn = 'off';
  }

  /** true gdy pies w zasięgu obiektu lub event już po podejściu (intention-done / toggle). */
  private dogInRangeOf(evt: InteractEvent): boolean {
    if (evt.type === 'intention-done' || evt.type === 'toggle' || evt.type === 'consume') {
      return true;
    }
    const pos = this.walk?.position;
    if (!pos) return true;
    return Math.hypot(pos.x - evt.x, pos.y - evt.y) <= 110;
  }

  private onInteract(evt: InteractEvent): void {
    // Worek karmy na miskę → nasyp (drag — bez podejścia)
    if (
      evt.type === 'snap' &&
      evt.snapZoneId === 'bowl' &&
      (evt.defId === 'house_food_bag' || evt.defId === 'house_treat')
    ) {
      this.handlePourFood();
      return;
    }

    if (evt.type === 'snap' && evt.snapZoneId === 'sandbox') {
      this.setStatus('Klocki na macie! 🧱');
      playFx(this, 'sparkle', evt.x, evt.y);
      return;
    }

    // Toggle-y: event 'toggle' leci z doToggle po executeAction (po zasięgu)
    if (evt.type === 'toggle' && evt.defId === 'house_fridge') {
      this.handleFridgeToggle(evt.state);
      return;
    }
    if (evt.type === 'toggle' && evt.defId === 'house_tv') {
      this.handleTvToggle(evt.state);
      return;
    }
    if (evt.type === 'toggle' && evt.defId === 'house_lamp') {
      this.handleLampToggle(evt.state);
      return;
    }

    // Tap / intention-done — tylko w zasięgu (tap poza zasięgiem czeka na intention-done)
    const isAct =
      evt.type === 'tap' || evt.type === 'intention-done';
    if (!isAct) return;
    if (evt.type === 'tap' && !this.dogInRangeOf(evt)) return;

    if (evt.defId === 'house_food_bag') {
      // tap (bez drag) — worek zostaje widoczny, podpowiedź jak nasypać karmę.
      this.setStatus('Przeciągnij karmę do miski! 🥣');
      return;
    }

    if (evt.defId === 'house_bowl') {
      this.handleFeedFromBowl(evt);
      return;
    }

    if (evt.defId === 'house_bed' && bedAction(evt.defId) === 'sleep') {
      // FX 'notes' odpala już ObjectLayer.executeAction (tap → obj.def.fx) — jedno źródło FX.
      void this.avatar?.playAction('sleep');
      this.setStatus('Piesek śpi… 💤');
      return;
    }

    if (isConsoleEntry(evt.defId)) {
      getSceneRouter().go('ConsoleHub');
      return;
    }

    if (evt.defId === 'house_blocks_box') {
      this.setStatus('Pudełko z klockami! Przeciągnij figurki na matę 🧱');
      return;
    }

    if (evt.defId === 'house_cat') {
      this.setStatus('Kotek mruczy! 🐱💖');
      return;
    }

    if (isWindow(evt.defId)) {
      this.spawnBird(evt.x, evt.y);
    }
  }

  private handlePourFood(): void {
    const prev = this.bowlFill;
    this.bowlFill = pourFoodIntoBowl(this.bowlFill);
    const bowl = this.objectLayer?.getObjects().find((o) => o.def.id === 'house_bowl');
    if (bowl && prev === 'empty' && this.bowlFill === 'full') {
      // empty → full
      if (bowl.state.state === 'empty') {
        bowl.doToggle();
      }
      playFx(this, 'crumbs', bowl.x, bowl.y);
      this.setStatus('Nasypano karmę do miski!');
    } else if (this.bowlFill === 'full') {
      this.setStatus('Miska jest pełna.');
    }
  }

  private handleFeedFromBowl(evt?: InteractEvent): void {
    const result = feedFromBowl(this.bowlFill);
    if (!result.ate) {
      this.setStatus('Najpierw nasyp karmę z worka do miski!');
      return;
    }
    this.bowlFill = result.bowl;
    const bowl = this.objectLayer?.getObjects().find((o) => o.def.id === 'house_bowl');
    // full → empty (advanceState cykl empty↔full; z full doToggle → empty).
    // FX 'hearts' odpala już ObjectLayer.executeAction (tap → obj.def.fx) — nie duplikuj tu.
    if (bowl && bowl.state.state === 'full') {
      bowl.doToggle();
    }
    void this.avatar?.playAction('eat');
    this.setStatus('Piesek je! 💖');

    // W9-B: krok misji house_feed_dog
    if (evt) {
      const mission = houseMissionFromInteract(evt);
      if (mission) {
        this.game.events.emit(INTERACT_BUS_EVENT, mission);
        this.events.emit('quest-progress', mission);
      }
    }
  }

  private handleFridgeToggle(state?: string): void {
    if (state === 'open' || state === 'closed') {
      this.fridgeDoor = state;
    } else {
      this.fridgeDoor = this.fridgeDoor === 'closed' ? 'open' : 'closed';
    }
    const n = fridgeTreatSpawnCount(this.fridgeDoor, this.fridgeTreatsSpawned, 3);
    if (n > 0) {
      const fridge = this.objectLayer?.getObjects().find((o) => o.def.id === 'house_fridge');
      const fx = fridge ? fridge.x : 520;
      const fy = fridge ? fridge.y : 340;
      const offsets = treatSpawnOffsets();
      this.objectLayer?.spawn(
        offsets.map((o) => ({
          defId: 'house_treat',
          x: fx + o.dx,
          y: fy + o.dy,
        })),
      );
      this.fridgeTreatsSpawned = true;
      this.setStatus('Wyjęto 3 smakołyki z lodówki!');
      playFx(this, 'sparkle', fx, fy);
    } else if (this.fridgeDoor === 'open') {
      this.setStatus('Lodówka otwarta.');
    } else {
      this.setStatus('Lodówka zamknięta.');
    }
  }

  private handleTvToggle(state?: string): void {
    if (state === 'on' || state === 'off') {
      this.tvOn = state;
    } else {
      this.tvOn = this.tvOn === 'on' ? 'off' : 'on';
    }
    this.applyTvVisual();
    this.setStatus(this.tvOn === 'on' ? 'Telewizor włączony 📺' : 'Telewizor wyłączony');
  }

  private applyTvVisual(): void {
    if (!this.tvScreen || !this.tvGlow) return;
    this.tweens.killTweensOf(this.tvScreen);
    this.tweens.killTweensOf(this.tvGlow);
    this.tvColorTimer?.remove(false);
    this.tvColorTimer = null;

    if (this.tvOn === 'on') {
      this.tvScreen.setFillStyle(0x4fc3f7, 1);
      this.tvGlow.setAlpha(0.45);
      this.tweens.add({
        targets: this.tvScreen,
        scaleX: 1.05,
        scaleY: 0.95,
        duration: 400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.InOut',
      });
      this.tweens.add({
        targets: this.tvGlow,
        alpha: 0.7,
        duration: 500,
        yoyo: true,
        repeat: -1,
      });
      this.tvColorTimer = this.time.addEvent({
        delay: 700,
        loop: true,
        callback: () => {
          if (this.tvOn !== 'on' || !this.tvScreen) return;
          const colors = [0x4fc3f7, 0xff8fab, 0xffd166, 0xa0e8af, 0xd4a5ff];
          const c = colors[Math.floor(Math.random() * colors.length)]!;
          this.tvScreen.setFillStyle(c, 1);
        },
      });
    } else {
      this.tvScreen.setScale(1);
      this.tvScreen.setFillStyle(0x111111, 1);
      this.tvGlow.setAlpha(0);
    }
  }

  private handleLampToggle(state?: string): void {
    if (state === 'on' || state === 'off') {
      this.lampOn = state;
    } else {
      this.lampOn = this.lampOn === 'on' ? 'off' : 'on';
    }
    if (this.lightDim) {
      this.lightDim.setAlpha(this.lampOn === 'on' ? 0 : 0.45);
    }
    this.setStatus(this.lampOn === 'on' ? 'Światło włączone 💡' : 'Światło wyłączone');
  }

  private spawnBird(fromX: number, fromY: number): void {
    // FX 'stars' odpala już ObjectLayer.executeAction — tu proceduralny ptaszek (bez emoji).
    const bird = this.add.container(fromX, fromY - 40).setDepth(8000);
    const body = this.add.ellipse(0, 0, 28, 20, 0x90caf9).setStrokeStyle(2, 0xffffff);
    const wing = this.add.ellipse(-6, 2, 16, 10, 0x64b5f6);
    const head = this.add.circle(12, -4, 8, 0xbbdefb).setStrokeStyle(1, 0xffffff);
    const beak = this.add.triangle(20, -2, 0, -4, 0, 4, 10, 0, 0xffb74d);
    bird.add([body, wing, head, beak]);
    this.setStatus('Ptaszek przyleciał! 🐦');
    this.tweens.add({
      targets: bird,
      x: fromX + 420,
      y: fromY - 120,
      duration: 2200,
      ease: 'Sine.InOut',
      onComplete: () => {
        this.tweens.add({
          targets: bird,
          x: HOUSE_WORLD_W + 40,
          y: fromY - 80,
          alpha: 0.4,
          duration: 1400,
          onComplete: () => bird.destroy(true),
        });
      },
    });
    this.tweens.add({
      targets: wing,
      scaleY: 0.4,
      duration: 120,
      yoyo: true,
      repeat: 16,
    });
  }

  private bindKeys(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    this.keys = {
      up: kb.addKey(Phaser.Input.Keyboard.KeyCodes.UP),
      down: kb.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN),
      left: kb.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT),
      right: kb.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT),
      w: kb.addKey(Phaser.Input.Keyboard.KeyCodes.W),
      a: kb.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      s: kb.addKey(Phaser.Input.Keyboard.KeyCodes.S),
      d: kb.addKey(Phaser.Input.Keyboard.KeyCodes.D),
    };
  }

  private bindFloorTap(): void {
    if (this.pointerBound) return;
    this.pointerBound = true;
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // nie ruszaj psa gdy hit na UI (scrollFactor 0 — ekran)
      if (pointer.y > this.gameH - 100) return;
      const hitObj = this.objectLayer?.getObjects().some((o) => {
        const b = o.container.getBounds();
        return b.contains(pointer.worldX, pointer.worldY);
      });
      if (hitObj) return;
      this.walk?.moveTo(pointer.worldX, pointer.worldY);
    });
  }

  private setStatus(msg: string): void {
    this.statusText?.setText(msg);
  }

  update(_t: number, dtMs: number): void {
    const dt = Math.min(0.05, dtMs / 1000);

    // WASD / strzałki → WalkController
    if (this.walk) {
      const { up, down, left, right, w, a, s, d } = this.keys;
      let vx = 0;
      let vy = 0;
      if (left?.isDown || a?.isDown) vx -= 1;
      if (right?.isDown || d?.isDown) vx += 1;
      if (up?.isDown || w?.isDown) vy -= 1;
      if (down?.isDown || s?.isDown) vy += 1;
      if (vx !== 0 || vy !== 0) {
        this.walk.setVelocityInput(vx, vy);
      }
      this.walk.update(dt);
    }

    if (this.avatar) {
      this.avatar.update(dt);
      // WalkController jest właścicielem pozycji — uniknij podwójnego kroku z velocity
      if (this.walk) {
        const p = this.walk.position;
        this.avatar.container.setPosition(p.x, p.y);
        this.avatar.container.setDepth(p.y + 20);
      }
    }

    this.objectLayer?.update(dt);
  }

  private async bootstrap(): Promise<void> {
    const loaded: HouseLoadResult = await loadHouseLayout({
      houseId: this.houseId ?? undefined,
    });
    if (!this.sys.isActive()) return;

    this.layout = loaded.layout;
    this.layoutVersion = loaded.layoutVersion;
    this.houseId = loaded.houseId;
    this.isOwner = loaded.isOwner;

    this.paintLayout();

    const src = loaded.source === 'server' ? 'z serwera' : 'zapis lokalny';
    const ownerHint = this.isOwner ? '' : ' · wizyta (tylko podgląd)';
    this.setStatus(
      `Domek gotowy (${src})${ownerHint} · tapuj przedmioty · WASD`,
    );
  }

  private drawFloorGrid(): void {
    const g = this.add.graphics().setDepth(-50);
    g.fillStyle(0xfff3e0, 0.35);
    g.fillRect(
      this.originX,
      this.originY,
      GRID_COLS * TILE_PX,
      GRID_ROWS * TILE_PX,
    );
    g.lineStyle(1, 0xffcc80, 0.25);
    for (let x = 0; x <= GRID_COLS; x++) {
      const wx = this.originX + x * TILE_PX;
      g.lineBetween(wx, this.originY, wx, this.originY + GRID_ROWS * TILE_PX);
    }
    for (let y = 0; y <= GRID_ROWS; y++) {
      const wy = this.originY + y * TILE_PX;
      g.lineBetween(this.originX, wy, this.originX + GRID_COLS * TILE_PX, wy);
    }
  }

  private tileCenter(tx: number, ty: number): { x: number; y: number } {
    const w = this.board.tileToWorld(tx, ty);
    return { x: this.originX + w.x, y: this.originY + w.y };
  }

  private paintLayout(): void {
    this.furnitureLayer?.removeAll(true);
    const sorted = [...this.layout].sort((a, b) => a.layer - b.layer || a.y - b.y);
    for (const b of sorted) {
      const { x, y } = this.tileCenter(b.x, b.y);
      const color = blockColor(b.id);
      const rect = this.add
        .rectangle(x, y, TILE_PX - 6, TILE_PX - 6, color, 0.55)
        .setStrokeStyle(2, 0xffffff, 0.5)
        .setOrigin(0.5);
      // pastelowy „kleks" zamiast emoji — podgląd układu z build mode
      const blob = this.add
        .circle(x, y - 4, 10, color, 0.95)
        .setStrokeStyle(2, 0xffffff, 0.7);
      const label = this.add
        .text(x, y + 18, blockLabelPl(b.id), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '10px',
          color: '#4a3f55',
        })
        .setOrigin(0.5);
      this.furnitureLayer?.add([rect, blob, label]);
    }
  }

  private paintNav(): void {
    const town = UiKit.button(this, {
      x: this.gameW / 2 - 160,
      y: this.gameH - 70,
      label: 'Wróć do miasteczka',
      fill: 0xffcc80,
      width: 240,
      onClick: () => getSceneRouter().go('Town'),
    });
    town.setScrollFactor(0).setDepth(20_001);

    if (this.isOwner) {
      const build = UiKit.button(this, {
        x: this.gameW / 2 + 160,
        y: this.gameH - 70,
        label: 'Buduj',
        fill: 0x80deea,
        onClick: () =>
          getSceneRouter().go('HouseBuild', {
            houseId: this.houseId ?? undefined,
            layout: this.layout,
            layoutVersion: this.layoutVersion,
          }),
      });
      build.setScrollFactor(0).setDepth(20_001);
    }

    // legenda sterowania
    this.add
      .text(12, 12, 'Dom · WASD / tap · przedmioty ≥64 px', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#6b5f7a',
        backgroundColor: '#ffffffaa',
        padding: { x: 8, y: 4 },
      })
      .setScrollFactor(0)
      .setDepth(20_001);
  }

  shutdown(): void {
    this.unsubLayer?.();
    this.unsubLayer = null;
    this.objectLayer?.destroy();
    this.objectLayer = null;
    this.walk?.destroy();
    this.walk = null;
    this.avatar?.destroy();
    this.avatar = null;
    this.interactTarget = null;
  }
}

export { HouseBuildScene } from './HouseBuildScene.js';
export type { HouseBuildSceneData } from './HouseBuildScene.js';
export {
  houseObjectDefs,
  housePlacements,
  houseSnapZones,
  houseMissingAssetKeys,
  houseShapeForTexture,
  houseTextureKeys,
  HOUSE_TEXTURE_SHAPES,
  HOUSE_WORLD_W,
  HOUSE_WORLD_H,
} from './objects.js';
export default HouseScene;
